/**
 * AuthAdapter on Supabase. OTP is sent and checked by the send_otp / verify_otp Edge Functions (WhatsApp first, SMS fallback; codes
 * are hashed in Postgres), so the channel and the rate limits are ours. A successful login returns a normal Supabase session
 * (access + refresh token) minted server side; this adapter keeps it in the KeyValueStore and refreshes it before it expires.
 *
 *   persist:false  ("find my booking" for customers) returns a short-lived lookup token and creates NO session.
 */
import { normalizeIsraeliPhone } from '../../domain/phone';
import { toIso } from '../../domain/time';
import type { AuthAdapter, Clock, KeyValueStore, OtpRequestResult, OtpVerifyResult, Session, Unsubscribe } from '../types';
import { RestError, type Rest } from './rest';

const KEY = 'auth.sb.session';

interface Stored extends Session { refreshToken: string }
export interface SupabaseAuthOptions {
  rest: Rest;
  kv: KeyValueStore;
  clock: Clock;
  /** Auth REST base for refresh/logout: `${SUPABASE_URL}/auth/v1`. */
  authUrl: string;
  anonKey: string;
  fetch?: typeof fetch;
}
export interface SupabaseAuth extends AuthAdapter {
  /** The JWT to send to PostgREST / Functions; undefined when signed out or expired and not refreshable. */
  getAccessToken(): string | undefined;
  /** Refreshes the access token now. Returns false when signed out. */
  refresh(): Promise<boolean>;
  /** Schedules refreshes before expiry (browser). Returns a stop function. */
  autoRefresh(): () => void;
}

export function createSupabaseAuth(o: SupabaseAuthOptions): SupabaseAuth {
  const listeners = new Set<(s: Session | undefined) => void>();
  const f = o.fetch ?? ((...a: Parameters<typeof fetch>) => globalThis.fetch(...a));
  let cache: Stored | undefined;
  let loaded = false;
  const read = (): Stored | undefined => {
    if (loaded) return cache;
    loaded = true;
    try { cache = JSON.parse(o.kv.get(KEY) ?? 'null') as Stored | null ?? undefined; } catch { cache = undefined; }
    return cache;
  };
  const write = (s: Stored | undefined): void => {
    cache = s; loaded = true;
    if (s) o.kv.set(KEY, JSON.stringify(s)); else o.kv.remove(KEY);
    const pub = s ? publicSession(s) : undefined;
    for (const cb of [...listeners]) cb(pub);
  };
  const publicSession = ({ refreshToken: _r, ...s }: Stored): Session => s;
  const fresh = (s: Stored | undefined): boolean => !!s && Date.parse(s.expiresAt) > o.clock.now();

  async function refresh(): Promise<boolean> {
    const cur = read();
    if (!cur) return false;
    try {
      const res = await f(`${o.authUrl}/token?grant_type=refresh_token`, {
        method: 'POST', headers: { apikey: o.anonKey, 'Content-Type': 'application/json' }, body: JSON.stringify({ refresh_token: cur.refreshToken }),
      });
      if (!res.ok) { if (res.status === 400 || res.status === 401) write(undefined); return false; }
      const j = await res.json() as { access_token: string; refresh_token: string; expires_at?: number; expires_in?: number };
      const expMs = j.expires_at ? j.expires_at * 1000 : o.clock.now() + (j.expires_in ?? 3600) * 1000;
      write({ ...cur, token: j.access_token, refreshToken: j.refresh_token, expiresAt: toIso(expMs) });
      return true;
    } catch { return false; }   // offline: keep the session, try again later
  }

  return {
    label: 'Phone verification (WhatsApp / SMS)',
    async requestOtp(phone): Promise<OtpRequestResult> {
      const p = normalizeIsraeliPhone(phone);
      if (!p.ok) return { ok: false, reason: 'invalid_phone' };
      try {
        const r = await o.rest.invoke<{ ok: boolean; reason?: 'rate_limited' | 'invalid_phone' | 'send_failed' }>('send_otp', { phone: p.e164 }, { auth: 'anon' });
        return r.ok ? { ok: true, phone: p.e164 } : { ok: false, reason: r.reason === 'invalid_phone' ? 'invalid_phone' : r.reason === 'send_failed' ? 'send_failed' : 'rate_limited' };
      } catch (e) {
        if (e instanceof RestError && e.status === 429) return { ok: false, reason: 'rate_limited' };
        throw e;
      }
    },
    async verifyOtp(phone, code, opts): Promise<OtpVerifyResult> {
      const p = normalizeIsraeliPhone(phone);
      if (!p.ok) return { ok: false, reason: 'invalid_phone' };
      const persist = opts?.persist !== false;
      const r = await o.rest.invoke<
        | { ok: true; accessToken: string; refreshToken?: string; expiresAt: number; ownerId?: string; businessId?: string }
        | { ok: false; reason: 'no_code' | 'wrong_code' | 'expired' | 'too_many_attempts' }
      >('verify_otp', { phone: p.e164, code: code.trim(), persist }, { auth: 'anon' });
      if (!r.ok) return { ok: false, reason: r.reason };
      const session: Session = {
        token: r.accessToken, phone: p.e164, createdAt: o.clock.nowIso(), expiresAt: toIso(r.expiresAt * 1000),
        ...(r.ownerId ? { ownerId: r.ownerId } : {}), ...(r.businessId ? { businessId: r.businessId } : {}),
      };
      if (persist) write({ ...session, refreshToken: r.refreshToken ?? '' });
      return { ok: true, session };
    },
    getSession: () => { const s = read(); return s && fresh(s) ? publicSession(s) : undefined; },
    linkSession(ownerId, businessId) { const s = read(); if (s) write({ ...s, ownerId, businessId }); },
    signOut() {
      const s = read();
      write(undefined);
      if (s) void f(`${o.authUrl}/logout`, { method: 'POST', headers: { apikey: o.anonKey, Authorization: `Bearer ${s.token}` } }).catch(() => undefined);
    },
    getDemoCode: () => undefined,
    subscribe(cb): Unsubscribe { listeners.add(cb); return () => void listeners.delete(cb); },
    getAccessToken: () => { const s = read(); return s && fresh(s) ? s.token : undefined; },
    refresh,
    autoRefresh() {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const arm = (): void => {
        const s = read();
        if (!s) { timer = setTimeout(arm, 30_000); return; }
        const wait = Math.max(5_000, Date.parse(s.expiresAt) - o.clock.now() - 60_000);
        timer = setTimeout(() => { void refresh().finally(arm); }, wait);
      };
      arm();
      return () => { if (timer) clearTimeout(timer); };
    },
  };
}
